'use client';

import type { FC } from 'react';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { RefObject } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useNavigation } from '../../contexts/NavigationContext';
import { ScreenContent, ScreenFixedTop, ScreenFixedBottom } from '../../screen';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button, ButtonGroup, EmptyState, TabFilter } from '../../components/ui';
import type { TabFilterTab } from '../../components/ui';
import { BattleCard, type BattleData } from '../../components/BattleCard';
import { AnimatedBattleList } from './components/AnimatedBattleList';
import { useActiveCaseBattles, useCaseBattleHistoryAllInfinite, useCaseBattleHistoryMineInfinite } from '../../queries/useCaseBattleQueries';
import { useCaseBattleSubscription } from '../../hooks/useCaseBattleSubscription';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import type { ScreenParams } from '../../types/navigation';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useIsAuthed } from '../../hooks/useIsAuthed';
import { LoginMethods } from '../../components/LoginMethods';
import translations from './CaseBattlesScreen.translations';

interface CaseBattlesScreenProps {
  params?: ScreenParams;
  onBack?: () => void;
}

type BattleFilter = 'active' | 'historyAll' | 'historyMine';

export const CaseBattlesScreen: FC<CaseBattlesScreenProps> = ({ params }) => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const isDesktop = useIsDesktop();
  const isAuthed = useIsAuthed();
  // Initial filter from navigation params; tracked back into params on change.
  const [filter, setFilter] = useState<BattleFilter>(
    (params?.filter as BattleFilter) || 'active',
  );

  // Sync filter state with navigation params when returning to this screen
  // (mirrors the inventory-screen pattern).
  const paramsFilter = params?.filter as BattleFilter | undefined;
  useEffect(() => {
    if (paramsFilter && paramsFilter !== filter) {
      setFilter(paramsFilter);
    }
  }, [paramsFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to all battle events for real-time list updates
  useCaseBattleSubscription(undefined, {
    onEvent: (event) => {
      console.log('[CaseBattlesScreen] Received battle event:', event.type);
    }
  });

  const {
    data: activeBattles,
    isLoading: isLoadingActive,
    error: activeError,
    refetch: refetchActive,
  } = useActiveCaseBattles(filter === 'active');

  const {
    data: historyAllData,
    isLoading: isLoadingHistoryAll,
    error: historyAllError,
    refetch: refetchHistoryAll,
    fetchNextPage: fetchNextHistoryAll,
    hasNextPage: hasNextHistoryAll,
    isFetchingNextPage: isFetchingNextHistoryAll,
  } = useCaseBattleHistoryAllInfinite(filter === 'historyAll');

  const {
    data: historyMineData,
    isLoading: isLoadingHistoryMine,
    error: historyMineError,
    refetch: refetchHistoryMine,
    fetchNextPage: fetchNextHistoryMine,
    hasNextPage: hasNextHistoryMine,
    isFetchingNextPage: isFetchingNextHistoryMine,
  } = useCaseBattleHistoryMineInfinite(filter === 'historyMine');

  const tabs: TabFilterTab<BattleFilter>[] = [
    { id: 'active', label: t('tabActive') },
    { id: 'historyAll', label: t('tabHistoryAll') },
    { id: 'historyMine', label: t('tabHistoryMine') },
  ];

  // Flatten paginated data
  const historyAllBattles = useMemo(() => {
    return historyAllData?.pages.flatMap(page => page.items) ?? [];
  }, [historyAllData]);

  const historyMineBattles = useMemo(() => {
    return historyMineData?.pages.flatMap(page => page.items) ?? [];
  }, [historyMineData]);

  // Determine current state based on filter
  const isLoading = filter === 'active'
    ? isLoadingActive
    : filter === 'historyAll'
      ? isLoadingHistoryAll
      : isLoadingHistoryMine;

  const error = filter === 'active'
    ? activeError
    : filter === 'historyAll'
      ? historyAllError
      : historyMineError;

  const battles: BattleData[] | undefined = filter === 'active'
    ? activeBattles
    : filter === 'historyAll'
      ? historyAllBattles
      : historyMineBattles;

  const refetch = filter === 'active'
    ? refetchActive
    : filter === 'historyAll'
      ? refetchHistoryAll
      : refetchHistoryMine;

  const hasMore = filter === 'historyAll'
    ? hasNextHistoryAll ?? false
    : filter === 'historyMine'
      ? hasNextHistoryMine ?? false
      : false;

  const isFetchingMore = filter === 'historyAll'
    ? isFetchingNextHistoryAll
    : filter === 'historyMine'
      ? isFetchingNextHistoryMine
      : false;

  const fetchNextPage = filter === 'historyAll'
    ? fetchNextHistoryAll
    : fetchNextHistoryMine;

  // Single scroll ref shared with infinite scroll, so we can reset position
  // on tab change. Mirrors InventoryScreen.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useInfiniteScroll({
    hasMore,
    loading: isFetchingMore,
    onLoadMore: fetchNextPage,
    threshold: 200,
    scrollRef: scrollRef as RefObject<HTMLDivElement | null>,
  });

  const handleFilterChange = useCallback((next: BattleFilter) => {
    setFilter(next);
    // Persist filter to navigation params so it's restored when returning.
    navigation.updateCurrentParams({ filter: next });
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [navigation]);

  // Track previous stack depth to detect when we return to this screen
  const prevStackDepthRef = useRef(0);

  // Refetch data when returning to this screen from battle details
  useEffect(() => {
    const currentScreen = navigation.getCurrentScreen();
    const currentStack = navigation.state.stacks[navigation.state.activeTab];
    const currentDepth = currentStack.length;

    // Only refetch if:
    // 1. We're on case-battles screen
    // 2. Stack depth decreased (we went back)
    // 3. We're on games tab
    if (
      currentScreen.screen === 'case-battles' &&
      prevStackDepthRef.current > currentDepth &&
      navigation.state.activeTab === 'games'
    ) {
      refetch();
    }

    // Update previous depth
    prevStackDepthRef.current = currentDepth;
  }, [navigation.state.stacks, navigation.state.activeTab, refetch, navigation]);

  // Refetch when filter changes (tab switching)
  useEffect(() => {
    refetch();
  }, [filter, refetch]);

  const handleBattleClick = (battleId: string) => {
    navigation.pushScreen('games', 'case-battle-details', { battleId });
  };

  const handleCreateClick = () => {
    navigation.pushScreen('games', 'case-battle-create');
  };

  const showHistoryMineLoginWall = filter === 'historyMine' && !isAuthed;

  const battleListContent = showHistoryMineLoginWall ? (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <p className="text-base text-white/70 mb-6 max-w-[320px] leading-snug">
        {t('historyMineLoginReason')}
      </p>
      <LoginMethods />
    </div>
  ) : (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <ErrorMessage
          error={error instanceof Error ? error.message : String(error)}
          onRetry={() => refetch()}
        />
      ) : (
        <div className="flex flex-col gap-2 min-w-0">
          <AnimatedBattleList
            key={filter}
            battles={battles}
            renderBattle={(battle) => (
              <BattleCard
                battle={battle}
                onClick={() => handleBattleClick(battle.id)}
              />
            )}
            renderEmpty={() => (
              <EmptyState message={filter === 'active' ? t('empty') : t('emptyHistory')} />
            )}
          />
          {isFetchingMore && (
            <div className="flex justify-center py-4">
              <LoadingSpinner size="small" />
            </div>
          )}
        </div>
      )}
    </>
  );

  if (isDesktop) {
    return (
      <ScreenContent scrollRef={scrollRef} scrollKey="case-battles">
        <div className="flex gap-6 p-6">
          {/* Left: sidebar */}
          <div className="w-[240px] shrink-0 sticky top-6 self-start">
            <div
              className="rounded-2xl border border-white/[0.06] flex flex-col overflow-hidden"
              style={{ background: 'linear-gradient(180deg, rgba(15,18,30,0.95) 0%, rgba(10,12,20,0.98) 100%)' }}
            >
              <div className="px-2 py-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`flex items-center w-full px-3 py-2.5 rounded-lg border-none cursor-pointer transition-colors text-left text-sm font-medium outline-none ${
                      filter === tab.id
                        ? 'bg-white/[0.08] text-white'
                        : 'bg-transparent text-white/50 hover:bg-white/[0.04] hover:text-white/70'
                    }`}
                    onClick={() => handleFilterChange(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="p-4 border-t border-white/[0.04]">
                <Button size="s" stretched variant="cta" onClick={handleCreateClick}>
                  {t('createBattle')}
                </Button>
              </div>
            </div>
          </div>

          {/* Right: battle list */}
          <div className="flex-1 min-w-0">
            {battleListContent}
          </div>
        </div>
      </ScreenContent>
    );
  }

  return (
    <>
      <ScreenFixedTop title={t('title')}>
        <TabFilter tabs={tabs} activeTab={filter} onChange={handleFilterChange} />
      </ScreenFixedTop>
      <ScreenContent scrollRef={scrollRef} scrollKey="case-battles">
        <div className="p-4">
          {battleListContent}
          <div style={{ height: 'calc(80px + var(--tg-viewport-safe-area-inset-bottom, 0px))' }} />
        </div>
      </ScreenContent>
      <ScreenFixedBottom>
        <ButtonGroup>
          <Button size="l" stretched variant="cta" onClick={handleCreateClick}>
            {t('createBattle')}
          </Button>
        </ButtonGroup>
      </ScreenFixedBottom>
    </>
  );
};
